import { AxiosError } from 'axios';
import { useQuery } from '@tanstack/react-query';
import { QUERY_CONFIG, API_ENDPOINTS, buildApiUrl } from '@/constants';
import { api } from '@/utils';

import type { SortingState, PaginationState } from '@tanstack/react-table';
import type { Campaign } from '@/components/campaigns/types';

interface UseFetchCampaignsInput {
    sorting: SortingState;
    pagination: PaginationState;
    search?: string;
    enabled?: boolean;
}

interface UseFetchCampaignsResponse {
    data: Campaign[];
    total: number;
    totalFiltered: number;
    limit: number;
    offset: number;
}

const DEFAULT_DATE_RANGE = 'last_30_days';

const buildApiParams = (
    { sorting, pagination, search }: UseFetchCampaignsInput,
    // Passed as created_at so the server filters call_logs by this range when computing avg scores.
    dateRange: string
): Record<string, string> => {
    const offset = pagination.pageIndex * pagination.pageSize;
    const limit = pagination.pageSize;

    const params: Record<string, string> = {
        offset: offset.toString(),
        limit: limit.toString(),
        created_at: dateRange,
    };

    if (sorting.length > 0) {
        const { id, desc } = sorting[0];
        params.sortField = id;
        params.sortOrder = desc ? 'desc' : 'asc';
    }

    if (search) {
        params.search = search;
    }

    return params;
};

const useFetchCampaigns = ({ sorting, pagination, search, enabled = true }: UseFetchCampaignsInput) => {
    const { data, status, isLoading, isError, error } = useQuery<UseFetchCampaignsResponse, AxiosError>({
        // Default date range used to calculate the average score for each project.
        queryKey: ['campaigns', sorting, pagination, search],
        queryFn: async () => {
            const params = buildApiParams({ sorting, pagination, search }, DEFAULT_DATE_RANGE);
            const url = buildApiUrl(API_ENDPOINTS.CAMPAIGNS, params);
            const response = await api.get(url);

            return response.data;
        },
        enabled,
        staleTime: 1000 * 60 * 30, // 30 minutes
        refetchOnWindowFocus: QUERY_CONFIG.REFETCH_ON_WINDOW_FOCUS,
        gcTime: 0,
        retry: 2,
    });

    return {
        data,
        status,
        isLoading,
        isError,
        error,
    };
};

export default useFetchCampaigns;
